// Expenses — staff expense claims & supplier receipts (plan/expenses.md + plan/rbac-approvals-expenses.md).
//
// The PREFERRED entry is the AI drag-and-drop: drop a receipt/supplier-invoice and it runs through
// doc_vision_service (POST /claims/extract) to auto-capture amount / merchant / date / tax, then
// prefills the form — the user just reviews & submits. Submitting WITHOUT a document is allowed
// (optional-doc); attachments (and notes) can be added at ANY stage. Lifecycle: submit →
// approve/reject → pay (reimburse, posts the journal) → mark-done. Paid expenses can be reconciled
// to an imported bank line (duplicate amounts are surfaced and the user confirms which line pairs).
//
// Capability-gated against the server's own predicate (GET /expense-access): a CAPTURER may submit
// and see claims but the approve/reject/pay/reconcile buttons only render for an APPROVER (admin or
// governor); the server still enforces it (a 403 shows inline) — the UI just doesn't offer what the
// API would refuse. Approving/rejecting also lives on the dedicated Approvals screen.
//
// Focus-safe (tina4 gotcha): the capture-form inputs bind BARE top-level signals (.value=${sig});
// no <input> lives inside a ${()=>…} slot that reads the signal it mutates. Every editing flow that
// needs a field mid-list (reject reason, pay, reconcile) uses a plain-DOM modal, so a row re-render
// never steals focus.
import { html, signal, effect } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { tenantShell } from './tshell.js';
import { t } from '../shared/i18n.js';
import { fmtMoney, formatDate, baseCurrencySymbol, baseCurrencyCode, isForeignAccount } from './bank_helpers.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { fmtRate } from '../shared/formats.js';
import { attachmentsCard } from '../shared/attachments.js';
import { notice } from '../shared/notice.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import '../shared/sleekdate.js';   // registers <sleek-date>

const STATUS_PILL = {
  pending: 'pill-due', review: 'pill-due', approved: 'pill-paid',
  reimbursed: 'pill-paid', rejected: 'pill-overdue', done: 'pill-void',
};
const STATUS_LABEL = {
  pending: ['expenses.st_pending', 'Awaiting review'], review: ['expenses.st_review', 'In review'],
  approved: ['expenses.st_approved', 'Approved'], reimbursed: ['expenses.st_paid', 'Paid'],
  rejected: ['expenses.st_rejected', 'Rejected'], done: ['expenses.st_done', 'Done'],
};
const stLabel = s => { const p = STATUS_LABEL[s] || [null, s]; return t(p[0], p[1]); };
const stClass = s => 'pill ' + (STATUS_PILL[s] || 'pill-due');

// tab → status query for the list endpoint
const TABS = [
  ['to_review', 'expenses.tab_review', 'To review', 'pending,review'],
  ['approved', 'expenses.tab_approved', 'Approved', 'approved'],
  ['paid', 'expenses.tab_paid', 'Paid', 'paid'],
  ['history', 'expenses.tab_history', 'History', 'done'],
  ['rejected', 'expenses.tab_rejected', 'Rejected', 'rejected'],
  ['all', 'common.all', 'All', ''],
];

// File → { name, mime_type, data(base64 no prefix), size } for the AI extract call.
function fileToInline(file){
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error || new Error('read_failed'));
    fr.onload = () => {
      const s = String(fr.result || ''), i = s.indexOf(',');
      resolve({ name: file.name, mime_type: file.type || 'application/octet-stream',
                data: i >= 0 ? s.slice(i + 1) : s, size: file.size || 0 });
    };
    fr.readAsDataURL(file);
  });
}

export function expensesScreen(companyId){
  const caps = signal(null);              // { is_approver, can_create, approval_required, … }
  const ref = signal(null);               // { currency_symbol, date_format }
  const accounts = signal([]), taxCodes = signal([]), banks = signal([]), contacts = signal([]);

  const rows = signal([]), summary = signal(null), loading = signal(true), err = signal('');
  const tab = signal('to_review'), q = signal(''), page = signal(1), perPage = signal(DEFAULT_PER_PAGE);
  const total = signal(0), totalPages = signal(1);
  const expanded = signal(null);          // claim id whose detail row is open
  const notesByClaim = signal({});        // claim_id -> [notes]
  const attachCards = {};                 // claim_id -> attachmentsCard view fn (created once)

  // capture form (bare top-level signals — focus-safe)
  const showForm = signal(false);
  const fDesc = signal(''), fAmount = signal(''), fSpentAt = signal(''), fDate = signal('');
  const fAccount = signal(''), fTax = signal(''), fContact = signal(''), fRebill = signal(false);
  const fReimb = signal(true), fNote = signal('');
  const submitBusy = signal(false), submitErr = signal('');
  const aiBusy = signal(false), aiMsg = signal('');

  const sym = () => (ref.value && ref.value.currency_symbol) || baseCurrencySymbol();
  // Expenses are claimed and paid in the company's own currency (owner 2026-10-19: no currency bleeds
  // into another): its code tells a foreign bank account or a foreign receipt from a base one.
  const baseCode = () => String(baseCurrencyCode() || (ref.value && ref.value.currency_code) || '').toUpperCase();
  const baseBanks = () => (banks.value || []).filter(b => !isForeignAccount(b, { code: baseCode() }));
  const dfmt = () => (ref.value && ref.value.date_format) || 'yyyy-MM-dd';
  const isApprover = () => !!(caps.value && caps.value.is_approver);
  const canCreate = () => !caps.value || caps.value.can_create;   // optimistic until it resolves
  const gateOn = () => !!(caps.value && caps.value.approval_required);

  // ── reference data ──
  (async () => {
    try {
      ref.value = await api.get('/companies/' + companyId + '/documents-reference');
      configureSleekDate({ date_format: ref.value.date_format, periods: ref.value.periods,
        fy_start_day: ref.value.fy_start_day, fy_start_month: ref.value.fy_start_month });
    } catch {}
  })();
  (async () => { try { caps.value = await api.get('/companies/' + companyId + '/expense-access'); } catch {} })();
  (async () => {
    try {
      const b = await api.get('/companies/' + companyId + '/settings');
      accounts.value = (b.accounts || []).filter(a => a.is_postable && !a.is_bank && !a.is_control);
      taxCodes.value = b.tax_codes || [];
    } catch {}
  })();
  (async () => { try { banks.value = (await api.get('/companies/' + companyId + '/bank-accounts')).data || []; } catch {} })();
  (async () => { try { contacts.value = (await api.get('/companies/' + companyId + '/contacts?per_page=200')).data || []; } catch {} })();

  let loadSeq = 0;
  async function load(){
    const seq = ++loadSeq;
    loading.value = true;
    const status = (TABS.find(x => x[0] === tab.value) || [])[3] || '';
    const qs = new URLSearchParams({ page: String(page.value), per_page: String(perPage.value) });
    if(q.value.trim()) qs.set('q', q.value.trim());
    if(status) qs.set('status', status);
    try {
      const r = await api.get('/companies/' + companyId + '/claims?' + qs.toString());
      if(seq !== loadSeq) return;
      rows.value = r.data || []; summary.value = r.summary || null;
      total.value = r.total || 0; totalPages.value = r.total_pages || 1; err.value = '';
    } catch(e){
      if(seq !== loadSeq) return;
      rows.value = []; err.value = (e.data && e.data.error) || e.message || t('expenses.err_load', 'Could not load expenses');
    }
    if(seq === loadSeq) loading.value = false;
  }
  effect(() => { tab.value; page.value; perPage.value; load(); });   // reload on tab/page/rows change
  let searchTimer;
  function onSearch(v){ q.value = v; clearTimeout(searchTimer); searchTimer = setTimeout(() => { page.value = 1; load(); }, 300); }

  function resetForm(){
    fDesc.value = ''; fAmount.value = ''; fSpentAt.value = ''; fDate.value = '';
    fAccount.value = ''; fTax.value = ''; fContact.value = ''; fRebill.value = false;
    fReimb.value = true; fNote.value = ''; submitErr.value = ''; aiMsg.value = '';
  }

  // ── AI drag-drop capture (preferred entry) ──
  async function extractFile(file){
    if(!file) return;
    aiBusy.value = true; aiMsg.value = t('expenses.ai_reading', 'Reading the receipt…'); submitErr.value = '';
    try {
      const f = await fileToInline(file);
      const res = await api.post('/companies/' + companyId + '/claims/extract', { file: f, kind: 'receipt' });
      const p = res.prefill || {};
      // A receipt in another currency: its figure is NOT the claim amount — the claim is in the
      // company's currency — so it is shown, not filled in (a $230 receipt must not become 230 rand).
      const rcpCode = String(p.currency || '').trim().toUpperCase();
      const otherCcy = !!(rcpCode && baseCode() && rcpCode !== baseCode() && p.amount != null);
      if(p.spent_at) fSpentAt.value = p.spent_at;
      if(p.amount != null && !otherCcy) fAmount.value = String(p.amount);
      if(p.claim_date) fDate.value = String(p.claim_date).slice(0, 10);
      if(p.description) fDesc.value = p.description;
      // default the tax code from the detected VAT rate when we can match one. A receipt is a purchase,
      // so only a purchase (input) code: the first code at 15% is the sales code "01 Output Tax".
      if(p.tax_implicated && !fTax.value){
        const m = (taxCodes.value || []).find(c => c.kind === 'input' && Number(c.rate) === Number(p.vat_rate));
        if(m) fTax.value = String(m.id);
      }
      showForm.value = true;
      aiMsg.value = otherCcy
        ? t('expenses.ai_other_currency', 'This receipt is in {code}: {amount}. Expenses are claimed in {base}, so enter the amount in {base}, then submit.')
            .replace('{code}', rcpCode).replace('{amount}', fmtMoney(p.amount, rcpCode)).replace(/\{base\}/g, baseCode())
        : t('expenses.ai_done', 'Captured — please check the amount and details, then submit.');
      notice(t('expenses.ai_ok', 'Receipt read — review and submit'), 'ok');
    } catch(e){
      const code = (e.data && e.data.error) || '';
      const map = {
        no_key: t('expenses.ai_no_key', 'AI reading is off — add your AI key in Settings → API Keys. You can still type it in.'),
        not_a_document: t('expenses.ai_not_doc', "That doesn't look like a receipt — type it in below."),
        nothing_found: t('expenses.ai_nothing', 'Nothing recognisable — try a sharper photo, or type it in.'),
        too_large: t('expenses.ai_large', 'That file is too big to read — type it in below.'),
      };
      aiMsg.value = map[code] || t('expenses.ai_failed', "Couldn't read that automatically — type the details in below.");
      showForm.value = true;
    }
    aiBusy.value = false;
  }
  function onDropOver(e){ e.preventDefault(); e.currentTarget.classList.add('is-dragover'); }
  function onDropLeave(e){ e.currentTarget.classList.remove('is-dragover'); }
  function onDrop(e){ e.preventDefault(); e.currentTarget.classList.remove('is-dragover');
    const dt = e.dataTransfer; if(dt && dt.files && dt.files[0]) extractFile(dt.files[0]); }
  function onDropPick(e){ const f = (e.target.files || [])[0]; e.target.value = ''; if(f) extractFile(f); }

  async function submit(){
    submitErr.value = '';
    if(!fDesc.value.trim()){ submitErr.value = t('expenses.need_desc', 'Add a description of what it was for'); return; }
    if(!(Number(fAmount.value) > 0)){ submitErr.value = t('expenses.need_amount', 'Enter an amount greater than zero'); return; }
    if(!fAccount.value){ submitErr.value = t('expenses.need_account', 'Choose the expense account'); return; }
    submitBusy.value = true;
    try {
      const body = { description: fDesc.value.trim(), amount: Number(fAmount.value),
        expense_account_id: Number(fAccount.value), spent_at: fSpentAt.value.trim() || undefined,
        claim_date: fDate.value || undefined, tax_code_id: fTax.value ? Number(fTax.value) : undefined,
        reimbursable: fReimb.value, note: fNote.value.trim() || undefined };
      if(fContact.value){ body.contact_id = Number(fContact.value); body.rebillable = fRebill.value; }
      const res = await api.post('/companies/' + companyId + '/claims', body);
      if(res && res.error){ submitErr.value = res.error; submitBusy.value = false; return; }
      resetForm(); showForm.value = false; submitBusy.value = false;
      notice(t('expenses.submitted', 'Expense submitted'), 'ok');
      page.value = 1; tab.value = 'to_review'; await load();
    } catch(e){
      submitErr.value = (e.data && e.data.error) || e.message || t('expenses.err_submit', 'Could not submit');
      submitBusy.value = false;
    }
  }

  // ── lifecycle actions (approver-gated; server re-enforces) ──
  async function act(path, body, okMsg){
    try {
      const res = await api.post('/companies/' + companyId + '/claims/' + path, body || {});
      if(res && res.error){ notice(res.error, 'bad'); return false; }
      if(okMsg) notice(okMsg, 'ok');
      await load();
      return true;
    } catch(e){ notice((e.data && e.data.error) || e.message || t('expenses.action_failed', 'Could not complete that'), 'bad'); return false; }
  }
  const approve = r => act(r.id + '/status', { status: 'approved' }, t('expenses.approved_ok', 'Expense approved'));
  const markDone = r => act(r.id + '/done', {}, t('expenses.done_ok', 'Marked done'));

  function openReject(r){
    modalReason({
      title: t('expenses.reject_title', 'Reject expense') + ' — ' + (r.spent_at || r.description || ''),
      onGo: async (reason) => act(r.id + '/reject', { reason }, t('expenses.rejected_ok', 'Expense rejected')),
    });
  }
  function openPay(r){
    // only the company-currency accounts: the API pays an expense from no other (no FX reimbursement)
    modalPay(r, baseBanks(), sym(), async (bankId, date) => {
      const res = await api.post('/companies/' + companyId + '/claims/' + r.id + '/reimburse',
        { bank_account_id: bankId, date: date || undefined });
      if(res && res.error) return res.error;
      notice(t('expenses.paid_ok', 'Expense paid'), 'ok'); await load(); return null;
    });
  }
  async function openReconcile(r){
    let data;
    try { data = await api.get('/companies/' + companyId + '/claims/' + r.id + '/bank-candidates'); }
    catch(e){ notice((e.data && e.data.error) || t('expenses.rec_failed', 'Could not load bank candidates'), 'bad'); return; }
    // a bank line in another currency is never this expense, whatever its digits: only lines on the
    // company-currency accounts are offered (the claim is in the company's currency)
    const ok = new Set(baseBanks().map(b => String(b.id)));
    const known = (banks.value || []).length > 0;
    const cands = ((data && data.candidates) || []).filter(c => !known || ok.has(String(c.bank_account_id)));
    modalReconcile(r, { ...(data || {}), candidates: cands, duplicate_amount: cands.length > 1 }, sym(), dfmt(), companyId, load);
  }

  // ── detail (attachments + notes) ──
  function attachCard(claimId){
    if(!attachCards[claimId]){
      attachCards[claimId] = attachmentsCard({
        base: '/companies/' + companyId + '/claims/' + claimId,
        attachmentBase: '/companies/' + companyId + '/claim-attachments',
        title: t('expenses.attachments', 'Attachments (receipts & proof of payment)'),
      });
    }
    return attachCards[claimId];
  }
  async function loadNotes(claimId){
    try {
      const r = await api.get('/companies/' + companyId + '/claims/' + claimId + '/notes');
      notesByClaim.value = { ...notesByClaim.value, [claimId]: r.data || [] };
    } catch { notesByClaim.value = { ...notesByClaim.value, [claimId]: [] }; }
  }
  function toggleExpand(r){
    if(expanded.value === r.id){ expanded.value = null; return; }
    expanded.value = r.id;
    attachCard(r.id);            // create the card once
    loadNotes(r.id);
  }
  function openAddNote(r){
    modalReason({
      title: t('expenses.add_note', 'Add a note'), label: t('expenses.note', 'Note'),
      ph: t('expenses.note_ph', 'e.g. Client trip — receipt to follow'),
      go: t('expenses.add', 'Add note'),
      onGo: async (body) => {
        try {
          const res = await api.post('/companies/' + companyId + '/claims/' + r.id + '/notes', { body });
          if(res && res.error) return res.error;
          await loadNotes(r.id); return null;
        } catch(e){ return (e.data && e.data.error) || e.message; }
      },
    });
  }

  // ── views ──
  const tabCount = key => {
    const s = summary.value; if(!s) return null;
    const c = s.counts || {};
    if(key === 'to_review') return (c.pending || 0) + (c.review || 0);
    if(key === 'approved') return c.approved || 0;
    if(key === 'paid') return c.reimbursed || 0;
    if(key === 'rejected') return c.rejected || 0;
    if(key === 'all') return s.all || 0;
    return null;   // history (done) not in the summary buckets
  };

  const captureCard = () => {
    if(!canCreate()) return '';
    const accs = accounts.value, tcs = taxCodes.value, cts = contacts.value;
    return html`<div class="card expense-capture">
      <div class="card-body">
        <div class="rail-card-title">${t('expenses.capture', 'Capture an expense')}</div>
        <div class="attach-drop expense-drop" @dragover=${onDropOver} @dragleave=${onDropLeave} @drop=${onDrop}>
          <span class="attach-drop-hint">🧾 ${t('expenses.drop_hint', 'Drop a receipt or supplier invoice here — the AI reads the amount & details in. Or')}</span>
          <button type="button" class="btn btn-sm btn-outline" ?disabled=${aiBusy.value} @click=${() => document.getElementById('exp-drop-input').click()}>${aiBusy.value ? t('expenses.ai_reading_btn', 'Reading…') : t('expenses.choose', 'Choose a file')}</button>
          <button type="button" class="btn btn-sm" @click=${() => { showForm.value = true; }}>${t('expenses.enter_manually', 'Enter manually')}</button>
          <input id="exp-drop-input" class="attach-file-input" type="file" accept="image/*,application/pdf"
            aria-label=${t('expenses.choose', 'Choose a file')} @change=${onDropPick}>
        </div>
        ${aiMsg.value ? html`<div class="hint expense-ai-msg">${aiMsg.value}</div>` : ''}
        ${showForm.value ? html`<div class="expense-form">
          <div class="grid2">
            <label class="fld"><span>${t('expenses.f_desc', 'What was it for')} *</span>
              <input class="form-control" .value=${fDesc} @input=${e => { fDesc.value = e.target.value; }} placeholder=${t('expenses.f_desc_ph', 'e.g. Client lunch — Ocean Basket')}></label>
            <label class="fld"><span>${t('expenses.f_amount', 'Amount')} *</span>
              <input class="form-control" type="number" step="0.01" min="0" .value=${fAmount} @input=${e => { fAmount.value = e.target.value; }} placeholder=${t('expenses.f_amount_ph', 'e.g. 230.00')}></label>
            <label class="fld"><span>${t('expenses.f_spent_at', 'Spent at (merchant)')}</span>
              <input class="form-control" .value=${fSpentAt} @input=${e => { fSpentAt.value = e.target.value; }} placeholder=${t('expenses.f_spent_at_ph', 'e.g. Engen Garage')}></label>
            <label class="fld"><span>${t('expenses.f_date', 'Spent on')}</span>
              <sleek-date class="form-control" .value=${fDate} @change=${e => { fDate.value = e.target.value; }}></sleek-date></label>
            <label class="fld"><span>${t('expenses.f_account', 'Expense account')} *</span>
              <select class="form-control" .value=${fAccount} @change=${e => { fAccount.value = e.target.value; }}>
                <option value="">${t('expenses.select_account', '— Select account —')}</option>
                ${accs.map(a => html`<option value=${String(a.id)}>${a.code} · ${a.name}</option>`)}
              </select></label>
            <label class="fld"><span>${t('expenses.f_tax', 'Tax (VAT / ITBMS)')}</span>
              <select class="form-control" .value=${fTax} @change=${e => { fTax.value = e.target.value; }}>
                <option value="">${t('expenses.no_tax', '— No tax —')}</option>
                ${tcs.map(c => html`<option value=${String(c.id)}>${c.code} · ${c.name} (${fmtRate(c.rate)}%)</option>`)}
              </select></label>
            <label class="fld"><span>${t('expenses.f_rebill', 'Rebill to customer (optional)')}</span>
              <select class="form-control" .value=${fContact} @change=${e => { fContact.value = e.target.value; }}>
                <option value="">${t('expenses.no_contact', '— None —')}</option>
                ${cts.map(c => html`<option value=${String(c.id)}>${c.name}</option>`)}
              </select></label>
            <label class="fld inline"><span>${t('expenses.f_reimbursable', 'My own money (reimburse me)')}</span>
              <input type="checkbox" class="chk" .checked=${fReimb} @change=${e => { fReimb.value = e.target.checked; }}></label>
          </div>
          ${fContact.value ? html`<label class="fld inline"><span>${t('expenses.f_rebillable', 'On-charge this to the customer later')}</span>
            <input type="checkbox" class="chk" .checked=${fRebill} @change=${e => { fRebill.value = e.target.checked; }}></label>` : ''}
          <label class="fld"><span>${t('expenses.f_note', 'Note for the approver (optional)')}</span>
            <textarea class="form-control" rows="2" .value=${fNote} @input=${e => { fNote.value = e.target.value; }} placeholder=${t('expenses.f_note_ph', 'e.g. Approved verbally by Sam')}></textarea></label>
          <div class="hint">${t('expenses.optional_doc', 'You can submit without a document — attach the receipt or proof of payment any time afterwards.')}</div>
          ${submitErr.value ? html`<div class="form-err">${submitErr.value}</div>` : ''}
          <div class="form-actions expense-form-actions">
            <button type="button" class="btn" @click=${() => { resetForm(); showForm.value = false; }}>${t('common.cancel', 'Cancel')}</button>
            <button type="button" class="btn primary" ?disabled=${submitBusy.value} @click=${submit}>${submitBusy.value ? t('expenses.submitting', 'Submitting…') : t('expenses.submit', 'Submit expense')}</button>
          </div>
        </div>` : ''}
      </div>
    </div>`;
  };

  const rowActions = r => {
    const A = isApprover();
    const out = [];
    if(A && (r.status === 'pending' || r.status === 'review')){
      out.push(html`<button type="button" class="btn sm primary" @click=${e => { e.stopPropagation(); approve(r); }}>${t('expenses.approve', 'Approve')}</button>`);
      out.push(html`<button type="button" class="btn sm bad" @click=${e => { e.stopPropagation(); openReject(r); }}>${t('expenses.reject', 'Reject')}</button>`);
      // gate OFF → an approver may pay a submitted claim directly (skip the approve step)
      if(!gateOn()) out.push(html`<button type="button" class="btn sm" @click=${e => { e.stopPropagation(); openPay(r); }}>${t('expenses.pay', 'Pay')}</button>`);
    }
    if(A && r.status === 'approved'){
      out.push(html`<button type="button" class="btn sm primary" @click=${e => { e.stopPropagation(); openPay(r); }}>${t('expenses.pay', 'Pay')}</button>`);
      out.push(html`<button type="button" class="btn sm bad" @click=${e => { e.stopPropagation(); openReject(r); }}>${t('expenses.reject', 'Reject')}</button>`);
    }
    if(A && r.status === 'reimbursed'){
      out.push(html`<button type="button" class="btn sm" @click=${e => { e.stopPropagation(); openReconcile(r); }}>${r.matched_bank_line_id ? t('expenses.reconciled', 'Reconciled ✓') : t('expenses.reconcile', 'Reconcile to bank')}</button>`);
      if(!r.done) out.push(html`<button type="button" class="btn sm primary" @click=${e => { e.stopPropagation(); markDone(r); }}>${t('expenses.mark_done', 'Mark done')}</button>`);
    }
    return out;
  };

  const detail = r => {
    const notes = (notesByClaim.value || {})[r.id] || [];
    return html`<tr class="expense-detail-row"><td colspan="6">
      <div class="expense-detail">
        <div class="expense-detail-facts">
          <span><b>${t('expenses.d_amount', 'Amount')}:</b> ${fmtMoney(r.amount, sym(), false)}</span>
          <span><b>${t('expenses.d_tax', 'Tax')}:</b> ${r.tax_code || t('expenses.none', '—')}</span>
          <span><b>${t('expenses.d_account', 'Account')}:</b> ${r.account_code || ''} ${r.account_name || ''}</span>
          <span><b>${t('expenses.d_status', 'Status')}:</b> ${stLabel(r.display_status || r.status)}</span>
          ${r.reject_reason ? html`<span class="expense-reject">“${r.reject_reason}”</span>` : ''}
          ${r.matched_bank_line_id ? html`<span><b>${t('expenses.d_matched', 'Bank match')}:</b> ${formatDate(r.matched_line_date, dfmt())} · ${r.matched_line_description || ''}</span>` : ''}
        </div>
        <div class="expense-detail-grid">
          ${attachCard(r.id)()}
          <div class="card expense-notes">
            <div class="card-body">
              <div class="rail-card-title">${t('expenses.notes', 'Notes')}
                ${canCreate() ? html`<button type="button" class="btn btn-sm btn-outline" @click=${() => openAddNote(r)}>${t('expenses.add_note_btn', 'Add note')}</button>` : ''}</div>
              ${notes.length ? html`<div class="row-list">${notes.map(n => html`<div class="row-card expense-note">
                  <span class="row-main">${n.body}<span class="row-sub">${n.created_by_name || ''}${n.created_at ? ' · ' + String(n.created_at).slice(0, 10) : ''}</span></span>
                </div>`)}</div>` : html`<div class="muted-block">${t('expenses.no_notes', 'No notes yet.')}</div>`}
            </div>
          </div>
        </div>
      </div>
    </td></tr>`;
  };

  const grid = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const body = rows.value, s = sym(), df = dfmt(), exp = expanded.value;
    if(!body.length) return html`<div class="empty">${q.value ? t('expenses.none_filtered', 'Nothing here for this filter.') : t('expenses.none', 'No expenses here yet.')}</div>`;
    return html`<div class="tbl-wrap"><table class="list expense-tbl">
      <thead><tr>
        <th>${t('expenses.col_date', 'Date')}</th>
        <th>${t('expenses.col_what', 'What / merchant')}</th>
        <th>${t('expenses.col_who', 'Person')}</th>
        <th class="ta-r">${t('expenses.col_amount', 'Amount')}</th>
        <th>${t('expenses.col_status', 'Status')}</th>
        <th class="ta-r">${t('expenses.col_action', 'Action')}</th>
      </tr></thead>
      <tbody>${body.map(r => {
        const acts = rowActions(r);
        return html`${html`<tr class="clickable" @click=${() => toggleExpand(r)}>
          <td class="mono">${formatDate(r.claim_date, df)}</td>
          <td><b>${r.spent_at || r.description}</b>${r.spent_at && r.description ? html`<span class="row-sub"> · ${r.description}</span>` : ''}
            ${r.attachment_count ? html`<span class="expense-clip" title=${t('expenses.has_files', 'Has attachments')}> 📎${r.attachment_count}</span>` : ''}</td>
          <td>${r.employee_name || '—'}</td>
          <td class="ta-r num">${fmtMoney(r.amount, s, false)}</td>
          <td><span class=${stClass(r.display_status || r.status)}>${stLabel(r.display_status || r.status)}</span></td>
          <td class="ta-r nowrap" @click=${e => e.stopPropagation()}>${acts.length ? acts : html`<span class="muted">—</span>`}</td>
        </tr>`}${exp === r.id ? detail(r) : ''}`;
      })}</tbody>
    </table></div>`;
  };

  const content = html`<div class="page page-wide expense-page">
    <div class="page-head">
      <div><span class="eyebrow">${t('nav.purchases', 'Purchases')}</span><h1>${t('nav.expenses', 'Expenses')}</h1>
        <p class="muted">${t('expenses.subtitle', 'Staff claims & supplier receipts — drop a receipt to capture it, submit, approve and pay.')}</p></div>
    </div>
    ${captureCard}
    <div class="list-toolbar">
      <input class="search ux-search" type="search" .value=${q} @input=${e => onSearch(e.target.value)}
        placeholder=${t('expenses.search_ph', 'e.g. Engen or 230')} aria-label=${t('expenses.search', 'Search expenses')}>
      <div class="segs">${TABS.map(([key, k, def]) => html`<button type="button" class=${'seg' + (tab.value === key ? ' on' : '')}
        @click=${() => { tab.value = key; page.value = 1; expanded.value = null; }}>${t(k, def)}${(() => { const n = tabCount(key); return n != null ? html` <span class="seg-n">${n}</span>` : ''; })()}</button>`)}</div>
      ${() => perPageSelect(perPage.value, e => { perPage.value = e.target.value; page.value = 1; })}
    </div>
    ${grid}
    ${() => pager({ page: page.value, totalPages: totalPages.value, total: total.value,
      perPage: Number(perPage.value), onPage: p => { page.value = p; } })}
  </div>`;

  return tenantShell(companyId, t('nav.expenses', 'Expenses'), content);
}

// ── plain-DOM modals (focus-safe: never a persistent input inside a reactive row) ──

function modalReason({ title, label, ph, go, onGo }){
  const node = html`<div class="modal-overlay"><div class="modal-panel">
    <div class="modal-title">${title}</div>
    <label class="fld"><span>${label || t('expenses.reason', 'Reason')} *</span>
      <textarea class="form-control" data-r rows="3" placeholder=${ph || t('expenses.reason_ph', 'e.g. Wrong amount — please resubmit')}></textarea></label>
    <div class="form-err" data-e hidden></div>
    <div class="modal-actions">
      <button type="button" class="btn" data-c>${t('common.cancel', 'Cancel')}</button>
      <button type="button" class="btn primary" data-g>${go || t('expenses.reject_send', 'Send')}</button>
    </div></div></div>`;
  const ov = node.querySelector('.modal-overlay'), ta = ov.querySelector('[data-r]');
  const eE = ov.querySelector('[data-e]'), gE = ov.querySelector('[data-g]');
  const close = () => ov.remove();
  ov.querySelector('[data-c]').addEventListener('click', close);
  gE.addEventListener('click', async () => {
    eE.hidden = true; const v = ta.value.trim();
    if(!v){ eE.hidden = false; eE.textContent = t('expenses.reason_required', 'This is required.'); ta.focus(); return; }
    gE.disabled = true;
    const errMsg = await onGo(v);
    if(errMsg){ eE.hidden = false; eE.textContent = errMsg; gE.disabled = false; return; }
    close();
  });
  document.body.appendChild(node);
  setTimeout(() => ta.focus(), 30);
}

function modalPay(r, banks, sym, onGo){
  const bank = { value: '' };
  const node = html`<div class="modal-overlay"><div class="modal-panel">
    <div class="modal-title">${t('expenses.pay_title', 'Pay expense')} — ${fmtMoney(r.amount, sym, false)}</div>
    <label class="fld"><span>${t('expenses.pay_from', 'Pay from bank account')} *</span>
      <select class="form-control" data-b @change=${e => { bank.value = e.target.value; }}>
        <option value="">${t('expenses.select_bank', '— Select bank —')}</option>
        ${(banks || []).map(b => html`<option value=${String(b.id)}>${b.name}${b.account_no ? ' · ' + b.account_no : ''}</option>`)}
      </select></label>
    <label class="fld"><span>${t('expenses.pay_date', 'Payment date')}</span>
      <sleek-date class="form-control" data-d></sleek-date></label>
    <div class="form-err" data-e hidden></div>
    <div class="modal-actions">
      <button type="button" class="btn" data-c>${t('common.cancel', 'Cancel')}</button>
      <button type="button" class="btn primary" data-g>${t('expenses.pay_go', 'Pay & post')}</button>
    </div></div></div>`;
  const ov = node.querySelector('.modal-overlay');
  const dE = ov.querySelector('[data-d]'), eE = ov.querySelector('[data-e]'), gE = ov.querySelector('[data-g]');
  const close = () => ov.remove();
  ov.querySelector('[data-c]').addEventListener('click', close);
  gE.addEventListener('click', async () => {
    eE.hidden = true;
    if(!bank.value){ eE.hidden = false; eE.textContent = t('expenses.pick_bank', 'Choose the bank account.'); return; }
    gE.disabled = true;
    const errMsg = await onGo(Number(bank.value), dE.value);
    if(errMsg){ eE.hidden = false; eE.textContent = errMsg; gE.disabled = false; return; }
    close();
  });
  document.body.appendChild(node);
}

function modalReconcile(r, data, sym, df, companyId, reload){
  const cands = (data && data.candidates) || [];
  const errSig = signal('');
  const close = () => ov.remove();
  async function link(lineId){
    errSig.value = '';
    try {
      const res = await api.post('/companies/' + companyId + '/claims/' + r.id + '/bank-link', { line_id: lineId });
      if(res && res.error){ errSig.value = res.error; return; }
      notice(t('expenses.linked_ok', 'Expense reconciled to the bank line'), 'ok'); close(); await reload();
    } catch(e){ errSig.value = (e.data && e.data.error) || e.message; }
  }
  async function unlink(){
    try { await api.post('/companies/' + companyId + '/claims/' + r.id + '/bank-unlink', {});
      notice(t('expenses.unlinked', 'Bank link removed'), 'ok'); close(); await reload(); }
    catch(e){ errSig.value = (e.data && e.data.error) || e.message; }
  }
  const node = html`<div class="modal-overlay"><div class="modal-panel expense-rec-modal">
    <div class="modal-title">${t('expenses.rec_title', 'Reconcile to a bank line')} — ${fmtMoney(r.amount, sym, false)}</div>
    ${data && data.duplicate_amount ? html`<div class="hint expense-dup-warn">${t('expenses.dup_warn', 'Several bank lines share this amount — pick the exact one so it is not mis-matched.')}</div>` : ''}
    ${r.matched_bank_line_id ? html`<div class="expense-currently"><b>${t('expenses.currently_linked', 'Currently linked to')}:</b> ${r.matched_line_description || ('#' + r.matched_bank_line_id)}
      <button type="button" class="btn btn-sm" @click=${unlink}>${t('expenses.unlink', 'Unlink')}</button></div>` : ''}
    <div class="row-list">${cands.length ? cands.map(c => html`<div class=${'row-card expense-cand' + (c.already_linked ? ' is-linked' : '')}>
        <span class="row-main"><b>${c.txn_date || ''}</b> · ${c.description || ''} <span class="row-sub">${c.reference || ''}${c.bank_name ? ' · ' + c.bank_name : ''} · ${fmtMoney(c.amount, sym, true)}</span></span>
        <span class="row-actions">${c.already_linked
          ? html`<span class="pill pill-void">${t('expenses.linked_elsewhere', 'Linked elsewhere')}</span>`
          : html`<button type="button" class="btn btn-sm primary" @click=${() => link(c.id)}>${t('expenses.link', 'This is it')}</button>`}</span>
      </div>`) : html`<div class="muted-block">${t('expenses.no_cands', 'No matching bank lines found for this amount and date.')}</div>`}</div>
    ${() => errSig.value ? html`<div class="form-err">${errSig.value}</div>` : ''}
    <div class="modal-actions"><button type="button" class="btn" data-c>${t('common.close', 'Close')}</button></div>
  </div></div>`;
  const ov = node.querySelector('.modal-overlay');
  ov.querySelector('[data-c]').addEventListener('click', close);
  document.body.appendChild(node);
}
